import {push as pushState} from 'react-router-redux'
import moment from 'moment'
import {getCurrentUser, getCurrentSite, getCurrentMerchant, getAggregatedSites} from './selectors'
import {closeModal} from 'redux/modules/ui/modal/actions'
import {updateSubscriptionCall, fetchBillingPlans} from 'redux/modules/billing/actions'
import {getPlans} from 'redux/modules/billing/selectors'
import {showToast} from 'redux/modules/ui/toaster/actions'
import {addSite, fetchSites} from 'redux/modules/sites/actions'
import {createMerchant, fetchMerchant} from 'redux/modules/merchants/actions'
import {reportError} from 'redux/modules/error/actions'
import {CALL_API, Call} from 'redux/middleware/api'
import {
  LOGIN,
  LOGOUT,
  SIGNUP,
  FORGOT,
  ROLES,
  EXCHANGE_TRACKER_TOKEN,
  CURRENT_USER,
  CURRENT_USER_FETCH_STATUS,
  CURRENT_USER_LOGGING_STATUS,
  CURRENT_USER_SET_TOKEN,
  CURRENT_USER_DELETE_TOKEN,
  CURRENT_USER_SET_SITE_ID,
  CURRENT_USER_SET_DATE_RANGE,
  CURRENT_USER_EDIT_PROFILE,
  CURRENT_USER_SET_PASSWORD,
  CURRENT_USER_SET_CONVERSION_TYPE,
  CURRENT_USER_SELECTED_SITES,
  CURRENT_USER_SET_TIME_PERIOD
} from './'
import {resources} from './utils'

function loginUserCall(creds) {
  return dispatch => {
    return dispatch({
      [CALL_API]: {
        endpoint: 'auth',
        errorsHandled: true,
        method: 'POST',
        types: LOGIN,
        body: {...creds}
      }
    })
      .success(response => {
        const {id: token} = response
        dispatch(saveToken(token))
      })
      .fail(dispatch(reportError()))
  }
}

export function loginUser(creds) {
  return dispatch => {
    dispatch({type: CURRENT_USER_LOGGING_STATUS, fetching: true})
    return new Call((success, fail) => {
      dispatch(loginUserCall(creds))
        .success(user => {
          dispatch({type: CURRENT_USER_LOGGING_STATUS, fetching: false})
          success(user)
        })
        .fail(error => {
          dispatch({type: CURRENT_USER_LOGGING_STATUS, fetching: false})
          fail(error)
        })
    })
  }
}

export function signupUser(creds) {
  return dispatch => {
    return dispatch({
      [CALL_API]: {
        endpoint: 'users',
        errorsHandled: true,
        method: 'POST',
        types: SIGNUP,
        body: {
          ...creds
        }
      }
    }).fail(dispatch(reportError()))
  }
}

function trackDivvitOrder(plan = {}, user, merchant) {
  try {
    if (window.divvit) {
      const {id = '0', name = 'unknown', amount = 0, currency = 'unknown'} = plan
      window.divvit.orderPlaced({
        order: {
          products: [{
            id,
            name,
            price: amount / 100,
            currency: currency.toUpperCase()
          }],
          orderId: merchant.id,
          total: amount / 100,
          currency: currency.toUpperCase(),
          customer: {
            idFields: {
              email: user.email
            },
            name: merchant.name
          }
        }
      })
    }
  } catch (error) {
    console.error(error)
  }
}

export function createMerchantAndSite(creds) {
  const {plan, url} = creds
  return (dispatch, getState) => {
    return new Call((success, fail) => {
      Call.all([
        dispatch(createMerchant(url)),
        dispatch(fetchBillingPlans())
      ])
        .success(() => {
          const state = getState()
          const currentMerchant = getCurrentMerchant(state)
          const currentUser = getCurrentUser(state)
          const plans = getPlans(state)

          return dispatch(updateSubscriptionCall(plan, currentMerchant.id))
            .success(() => {
              trackDivvitOrder(plans.find(p => p.id === plan), currentUser, currentMerchant)
              dispatch(fetchMerchant()).success(() => {
                dispatch(addSite(creds)).success(success).fail(fail)
              })
            })
        })
        .fail(dispatch(reportError()))
        .fail(fail)
    })
  }
}

export function logoutUser() {
  return dispatch => {
    return dispatch({
      [CALL_API]: {
        endpoint: 'auth',
        errorsHandled: true,
        method: 'DELETE',
        types: LOGOUT
      }
    })
      .success(() => dispatch(deleteToken()))
      // If logout failed delete token anyway :)
      .fail(() => dispatch(deleteToken()))
  }
}

export function forgotPassword(email) {
  return dispatch => {
    return dispatch({
      [CALL_API]: {
        endpoint: 'users/reset-password',
        errorsHandled: true,
        method: 'POST',
        types: FORGOT,
        body: {email}
      }
    })
      .success(() => {
        dispatch(showToast(`We've sent further instruction to ${email}`))
        dispatch(pushState('/login'))
      })
      .fail(dispatch(reportError()))
  }
}

export function saveToken(token) {
  localStorage.setItem('token', token)

  return {
    type: CURRENT_USER_SET_TOKEN,
    token
  }
}

export function saveCrossSiteSites(sites) {
  return {
    type: CURRENT_USER_SELECTED_SITES,
    sites
  }
}

export function deleteToken() {
  localStorage.removeItem('token')
  localStorage.removeItem('fromAdmin')
  localStorage.removeItem('siteId')
  if (window.Intercom) {
    window.Intercom('shutdown')
  }

  return {
    type: CURRENT_USER_DELETE_TOKEN
  }
}

export function fetchUser() {
  return {
    [CALL_API]: {
      endpoint: 'user',
      errorsHandled: true,
      types: CURRENT_USER
    }
  }
}

export function fetchPermissions() {
  return dispatch => dispatch({
    [CALL_API]: {
      endpoint: 'roles',
      errorsHandled: true,
      method: 'POST',
      types: ROLES,
      body: {
        resources,
        methods: ['read', 'create', 'show']
      }
    }
  })
}

export function fetchCurrentUser() {
  return (dispatch, getState) => {
    dispatch({type: CURRENT_USER_FETCH_STATUS, fetching: true})
    return Call.all([
      dispatch(fetchUser()),
      dispatch(fetchMerchant())
    ])
      .success(() => {
        const merchant = getCurrentMerchant(getState())
        return merchant ? dispatch(fetchSites()) : Call.resolve()
      })
      .success(() => {
        dispatch({type: CURRENT_USER_FETCH_STATUS, fetching: false})
      })
      .fail(() => {
        dispatch({type: CURRENT_USER_FETCH_STATUS, fetching: false})
      })
  }
}

export function selectSite(siteId, redirect = true) {
  localStorage.setItem('siteId', siteId)

  return (dispatch, getState) => {
    dispatch({
      type: CURRENT_USER_SET_SITE_ID,
      siteId
    })
    if (redirect) dispatch(pushState('/'))
  }
}

export function selectGlobalDateRange(startDate, endDate, dynamicDateType = false, range) {
  let newStartDate = moment(startDate).toDate()
  let newEndDate = moment(endDate).toDate()

  localStorage.setItem('startDate', moment(newStartDate).format())
  localStorage.setItem('endDate', moment(newEndDate).format())

  if (dynamicDateType) {
    localStorage.setItem('dynamicDateType', dynamicDateType)
  } else {
    localStorage.removeItem('dynamicDateType')
  }
  // set range
  return {
    type: CURRENT_USER_SET_DATE_RANGE,
    startDate: newStartDate,
    endDate: newEndDate,
    dynamicDateType,
    selectedTimePeriod: range
  }
}

export function getCurrentSitePlease(dispatch, getState, crossSiteMode) {
  const state = getState()
  const merchantSite = getCurrentSite(state)

  if (merchantSite) {
    return Call.resolve(getAggregatedSites(state, crossSiteMode))
  } else {
    return dispatch(fetchCurrentUser())
      .success(() => {
        return getAggregatedSites(state, crossSiteMode)
      })
  }
}

export function updateUserProfile({name, email}) {
  return (dispatch, getState) => {
    const currentUser = getCurrentUser(getState())

    return dispatch({
      [CALL_API]: {
        endpoint: `users/${currentUser.id}`,
        errorsHandled: true,
        method: 'PATCH',
        types: CURRENT_USER_EDIT_PROFILE,
        body: {name, email}
      }
    })
      .success(() => {
        dispatch(closeModal())
        dispatch(showToast('Profile has been updated'))
      })
      .fail(dispatch(reportError()))
  }
}

export function setPassword({uid, token, password}) {
  return dispatch => {
    return dispatch({
      [CALL_API]: {
        endpoint: `users/${uid}/set-password`,
        errorsHandled: true,
        method: 'POST',
        types: CURRENT_USER_SET_PASSWORD,
        body: {
          token,
          password
        }
      }
    })
      .success(user => {
        dispatch(showToast('New password has been set'))
        return dispatch(loginUser({email: user.email, password})).fail(dispatch(reportError()))
      })
      .fail(dispatch(reportError()))
  }
}

export function setTimePeriod({timePeriod, startRange, endRange, dynamicDateType}) {
  return {
    type: CURRENT_USER_SET_TIME_PERIOD,
    data: {timePeriod, startRange, endRange, dynamicDateType}
  }
}

export function setConversionType(conversionType) {
  return {
    type: CURRENT_USER_SET_CONVERSION_TYPE,
    data: {conversionType}
  }
}

export function exchangeTrackerToken({frontendId, trackerToken, email}) {
  return dispatch => {
    return dispatch({
      [CALL_API]: {
        method: 'POST',
        endpoint: 'auth/token',
        errorsHandled: true,
        types: EXCHANGE_TRACKER_TOKEN,
        body: {
          frontendId,
          trackerToken,
          email
        }
      }
    })
      .success(response => {
        const {id: token} = response
        dispatch({
          type: CURRENT_USER_SET_TOKEN,
          token
        })
      })
      .fail(dispatch(reportError()))
  }
}



// WEBPACK FOOTER //
// ./client/redux/modules/currentUser/actions.js